<template>
  <div class="control-validation-information">
    <h6>
      Validation Rules
      <span class="add-rule-btn"
            title="Click to add a new validation rule"
            @click="addNewRule">
        <i class="fa fa-plus-circle"></i>
      </span>
    </h6>

    <!--
      This field is saved to an Open Dental phone / SSN column, so we already know what
      shape the value has to be. Offer the matching rule; never add it behind the office's
      back. See libraries/od-format-rule-hint.js.
    -->
    <div v-if="suggestedFormatRule" class="od-format-rule-hint">
      <span class="od-format-rule-text">
        This field saves to Open Dental's {{ suggestedFormatRule.odFieldLabel }}.
        Add the {{ suggestedFormatRule.label }} rule so patients can't submit an invalid value.
      </span>
      <button type="button"
              class="od-format-rule-add"
              @click="addSuggestedFormatRule">
        Add rule
      </button>
    </div>

    <div v-if="!control.validations || control.validations.length === 0" class="no-rules-hint">
      No validation rules added. Click the + button above to add a rule.
    </div>

    <div class="validation-rule"
         v-for="(addedRule, ruleIndex) in control.validations"
         :key="ruleIndex">

      <div class="rule-header">
        <span class="rule-number">Rule {{ ruleIndex + 1 }}</span>
        <span class="remove-rule-btn"
              title="Remove this rule"
              @click="removeRule(ruleIndex)">
          <i class="fa fa-times"></i>
        </span>
      </div>

      <div class="form-group">
        <label>Validation Rule</label>
        <select class="form-control"
                @change="updateDefaultErrorMessage(addedRule)"
                v-model="addedRule.ruleType">
          <option value="" disabled>Choose a Rule</option>
          <option v-for="(ruleName, idx) in getRuleList(addedRule.ruleType)"
                  :key="idx"
                  :value="ruleName">
            {{ getRuleInfo(ruleName, 'label') || ruleName }}
          </option>
        </select>
        <small v-if="getRuleInfo(addedRule.ruleType, 'desc')" class="rule-description">
          {{ getRuleInfo(addedRule.ruleType, 'desc') }}
        </small>
      </div>

      <div class="form-group" v-if="getRuleInfo(addedRule.ruleType, 'needValue')">
        <label>Rule Value</label>
        <input type="text"
               class="form-control"
               :placeholder="getRuleInfo(addedRule.ruleType, 'valueInfo')"
               v-model="addedRule.additionalValue">
      </div>

      <div class="form-group">
        <label>Error Message</label>
        <input type="text"
               class="form-control"
               placeholder="Custom error message"
               v-model="addedRule.errorMessage">
      </div>
    </div>

  </div>
</template>

<script>
import {VALIDATION_RULES, ValidationRule} from "../../../configs/validation";
import {getSuggestedFormatRule} from "../../../libraries/od-format-rule-hint";

export default {
  name: 'ControlValidationInformation',
  props: {
    control: { type: Object, required: true },

    /**
     * Rule keys this control is allowed to offer. `null` (the default) means every rule in
     * VALIDATION_RULES. Comes from CONTROLS[type].allowedValidationRules — see controls.js.
     */
    allowedRules: { type: Array, default: null },

    /**
     * Per-control override of the hint text under the rule dropdown, keyed by rule name.
     * Comes from CONTROLS[type].validationRuleDescriptions.
     */
    ruleDescriptions: { type: Object, default: null }
  },

  computed: {
    /** Rule keys this control may use, in VALIDATION_RULES order. */
    ruleList() {
      const all = Object.keys(VALIDATION_RULES)
      return this.allowedRules ? all.filter(r => this.allowedRules.includes(r)) : all
    },

    /**
     * The format rule this control's Open Dental mapping implies but doesn't have yet,
     * or null. Reactive on control.validations, so the tip disappears the moment the
     * rule is added (by the button below or by hand).
     */
    suggestedFormatRule() {
      return getSuggestedFormatRule(this.control, this.allowedRules)
    }
  },

  methods: {
    /**
     * Get the rule info based on the validation rule
     * @param ruleName
     * @param ruleKey
     * @returns {boolean|string}
     */
    getRuleInfo(ruleName, ruleKey) {
      if (!ruleName || !VALIDATION_RULES[ruleName]) {
        return false
      }
      // A control can restate what a rule means in its own terms ("Minimum number of files"
      // reads better than "Minimum length of the value") without forking VALIDATION_RULES.
      if (ruleKey === 'desc' && this.ruleDescriptions && this.ruleDescriptions[ruleName]) {
        return this.ruleDescriptions[ruleName]
      }
      return VALIDATION_RULES[ruleName][ruleKey]
    },

    /**
     * Every time the rule-select is changed, update the default error message.
     */
    updateDefaultErrorMessage(addedRule) {
      addedRule.errorMessage = this.getRuleInfo(addedRule.ruleType, 'errorMessage')
    },

    /**
     * Add a new rule into validation array
     */
    addNewRule() {
      if (!this.control.validations) {
        this.$set(this.control, 'validations', [])
      }
      this.control.validations.push(new ValidationRule())
    },

    /**
     * Add the rule the Open Dental mapping implies. Same path as addNewRule() so the
     * default error message is filled in — and, like every other edit in this panel, it
     * lands on the sidebar's cloneDeep and only persists when the office hits Save.
     */
    addSuggestedFormatRule() {
      const suggestion = this.suggestedFormatRule
      if (!suggestion) {
        return
      }
      if (!this.control.validations) {
        this.$set(this.control, 'validations', [])
      }
      this.control.validations.push(new ValidationRule(suggestion.ruleType))
    },

    /**
     * Remove a rule
     */
    removeRule(index) {
      this.control.validations.splice(index, 1)
    },

    /**
     * Get all available rules without the ones already used (except current rule)
     * @param currentRule - the currently selected rule (to keep it in the list)
     * @return {String[]}
     */
    getRuleList(currentRule) {
      const allUsedRules = (this.control.validations || [])
        .map(rule => rule.ruleType)
        .filter(ruleName => ruleName !== currentRule)
      return this.ruleList.filter(rule => !allUsedRules.includes(rule))
    },
  }
}
</script>

<style scoped>
.control-validation-information h6 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-weight: 600;
  color: #374151;
}

.add-rule-btn {
  cursor: pointer;
  color: #10b981;
  font-size: 18px;
  transition: color 0.2s;
}

.add-rule-btn:hover {
  color: #059669;
}

/* Open Dental format-rule tip — an offer, so it reads as a note and not as an error. */
.od-format-rule-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px;
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: #78350f;
}

.od-format-rule-text {
  flex: 1;
}

.od-format-rule-add {
  flex-shrink: 0;
  padding: 4px 10px;
  background-color: #f59e0b;
  border: none;
  border-radius: 4px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.od-format-rule-add:hover {
  background-color: #d97706;
}

.no-rules-hint {
  padding: 12px;
  background-color: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
}

.validation-rule {
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.rule-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.rule-number {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
}

.remove-rule-btn {
  cursor: pointer;
  color: #ef4444;
  font-size: 14px;
  transition: color 0.2s;
}

.remove-rule-btn:hover {
  color: #dc2626;
}

.form-group {
  margin-bottom: 10px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select.form-control {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 20px;
  padding-right: 32px;
  appearance: none;
}

.rule-description {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #9ca3af;
  font-style: italic;
}
</style>
